<template>
  <div class="card border mb-3">
    <div
      class="card-header d-flex justify-content-between align-items-center flex-wrap gap-2"
    >
      <h5 class="mb-0">{{ $t("forwards.deals.editor.title") }}</h5>
      <div class="d-flex align-items-center gap-2">
        <button
          type="button"
          class="btn btn-sm btn-ghost"
          :disabled="busy"
          @click="discard"
        >
          {{ $t("forwards.deals.editor.discard") }}
        </button>
        <ActionButtonWithSpinner
          :busy="busy"
          :disabled="!canSave || busy"
          variant="btn-primary"
          @click="emit('save', planState)"
        >
          {{ $t("forwards.deals.editor.save") }}
        </ActionButtonWithSpinner>
      </div>
    </div>

    <div class="card-body">
      <div
        v-for="warning in dealWarnings"
        :key="warning.detail"
        class="advisory-note mb-2"
        role="status"
      >
        <FontAwesomeIcon icon="fa-regular fa-circle-info" />
        <span>{{ warning.detail }}</span>
      </div>

      <p
        v-if="correctsHistory"
        class="text-body-tertiary fs-10 mb-3"
        data-testid="correction-hint"
      >
        {{ $t("forwards.deals.correction.editor_hint") }}
      </p>

      <p v-if="errorMessage" class="error-note mb-3" role="alert">
        <FontAwesomeIcon icon="fa-solid fa-exclamation-triangle" />
        <span>{{ errorMessage }}</span>
      </p>

      <div
        v-for="delivery in visibleDeliveries"
        :key="delivery.key"
        class="border rounded p-3 mb-3"
      >
        <div class="row g-2 align-items-end">
          <div class="col-md-3">
            <label class="form-label" :for="`${delivery.key}-start`">{{
              $t("forwards.deals.editor.window_start")
            }}</label>
            <input
              :id="`${delivery.key}-start`"
              v-model="delivery.windowStart"
              type="date"
              class="form-control form-control-sm"
              :max="delivery.windowEnd || undefined"
              :disabled="fieldsLocked(delivery)"
            />
          </div>
          <div class="col-md-3">
            <label class="form-label" :for="`${delivery.key}-end`">{{
              $t("forwards.deals.editor.window_end")
            }}</label>
            <input
              :id="`${delivery.key}-end`"
              v-model="delivery.windowEnd"
              type="date"
              class="form-control form-control-sm"
              :class="{ 'is-invalid': delivery.windowEnd === '' }"
              :min="delivery.windowStart || undefined"
              :disabled="fieldsLocked(delivery)"
            />
          </div>
          <div class="col-md-3">
            <label class="form-label" :for="`${delivery.key}-price`">{{
              $t("forwards.deals.editor.unit_price")
            }}</label>
            <input
              :id="`${delivery.key}-price`"
              v-model="delivery.unitPrice"
              type="number"
              step="0.01"
              min="0"
              class="form-control form-control-sm"
              :disabled="fieldsLocked(delivery)"
            />
          </div>
          <div class="col-md-3 text-end">
            <span
              v-if="delivery.mode !== 'open'"
              class="badge rounded-pill bg-secondary-subtle text-secondary-emphasis"
              >{{
                $t(
                  `forwards.deals.transaction_status.${delivery.status ?? "voided"}`
                )
              }}</span
            >
            <button
              v-else-if="canPlan"
              type="button"
              class="btn btn-sm btn-ghost"
              @click="removeDelivery(delivery)"
            >
              {{ $t("forwards.deals.editor.remove_delivery") }}
            </button>
          </div>
        </div>

        <div
          v-for="warning in correctionCaveatsFor(delivery)"
          :key="warning"
          class="advisory-note mt-2"
          role="status"
          data-testid="correction-warning"
        >
          <FontAwesomeIcon icon="fa-solid fa-exclamation-triangle" />
          <span>{{ warning }}</span>
        </div>

        <div
          v-for="warning in warningsFor(delivery)"
          :key="warning.detail"
          class="advisory-note mt-2"
          role="status"
        >
          <FontAwesomeIcon icon="fa-regular fa-circle-info" />
          <span>{{ warning.detail }}</span>
        </div>

        <p
          v-for="change in changesFor(delivery)"
          :key="change"
          class="text-body-tertiary fs-10 mt-1 mb-0"
        >
          {{ change }}
        </p>

        <table class="table table-sm mt-3 mb-0">
          <thead>
            <tr>
              <th scope="col">{{ $t("forwards.deals.plan.period") }}</th>
              <th scope="col" class="volume-cell">
                {{ $t("forwards.common.planned") }}
              </th>
              <th scope="col" class="text-end"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in delivery.rows" :key="row.key">
              <td class="text-nowrap">
                <template v-if="row.isNew">
                  <input
                    v-model="row.periodLabel"
                    type="text"
                    class="form-control form-control-sm"
                    :class="{ 'is-invalid': periodLabelError(delivery, row) }"
                    :placeholder="
                      $t('forwards.deals.editor.period_placeholder')
                    "
                    :aria-label="$t('forwards.deals.plan.period')"
                  />
                  <div
                    v-if="periodLabelError(delivery, row) === 'duplicate'"
                    class="text-danger fs-10"
                  >
                    {{ $t("forwards.deals.editor.duplicate_period") }}
                  </div>
                </template>
                <template v-else>{{ row.periodLabel }}</template>
              </td>
              <td class="volume-cell">
                <MwhInputComponent
                  v-model="row.planned"
                  size="sm"
                  :min="row.backedMwh"
                  :invalid="isBelowBacked(row)"
                  :disabled="periodsLocked(delivery)"
                  :ariaLabel="
                    $t('forwards.deals.editor.planned_for', {
                      period: row.periodLabel,
                    })
                  "
                />
                <div v-if="isBelowBacked(row)" class="text-danger fs-10">
                  {{
                    $t("forwards.deals.editor.below_backed", {
                      mwh: formatNumberWithSpaces(row.backedMwh),
                    })
                  }}
                </div>
                <div
                  v-else-if="row.backedMwh > 0"
                  class="text-body-tertiary fs-10"
                >
                  {{
                    $t("forwards.deals.editor.backed_caption", {
                      mwh: formatNumberWithSpaces(row.backedMwh),
                    })
                  }}
                </div>
              </td>
              <td class="text-end">
                <!-- A disabled button gets no pointer events, so the reason hangs on the wrapper. -->
                <span
                  v-if="!periodsLocked(delivery)"
                  class="d-inline-block"
                  :title="removeBlockedReason(row)"
                >
                  <button
                    type="button"
                    class="btn btn-sm btn-ghost"
                    :disabled="row.backedMwh > 0"
                    @click="removePeriod(delivery, row)"
                  >
                    {{ $t("forwards.deals.plan.remove") }}
                  </button>
                </span>
              </td>
            </tr>
          </tbody>
        </table>

        <button
          v-if="!periodsLocked(delivery)"
          type="button"
          class="btn btn-sm btn-ghost mt-2"
          @click="addPeriod(delivery)"
        >
          {{ $t("forwards.deals.editor.add_period") }}
        </button>
      </div>

      <button
        v-if="canPlan"
        type="button"
        class="btn btn-sm btn-ghost"
        @click="addDelivery"
      >
        {{ $t("forwards.deals.editor.add_delivery") }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import ActionButtonWithSpinner from "@/components/common/ActionButtonWithSpinner.vue";
import { useUnsavedChangesGuard } from "@/composables/useUnsavedChangesGuard";
import { formatNumberWithSpaces } from "@/composables/formatUtils";
import MwhInputComponent from "@/forwards2/components/MwhInputComponent.vue";
import { isDeliveryFrozen } from "@/forwards2/deals/deliveryFrozen";
import { correctionCaveats } from "@/forwards2/deals/historyCorrection";
import { deliveryStateOf } from "@/forwards2/deals/planState";
import type {
  DealResponse,
  DeliveryResponse,
  PlanApplyResult,
  PlanDeliveryState,
  PlanStateRequest,
  PlanWarning,
  TransactionStatus,
} from "@/forwards2/forwardsTypes";
import {
  parsePeriodLabel,
  periodEndDate,
  periodStartDate,
} from "@/forwards2/periodUtils";
import { debounce, isEqual } from "lodash-es";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

/** Plan §6 WP-09b: the dry run behind the warnings runs 400 ms after the last keystroke. */
const DRY_RUN_DEBOUNCE_MS = 400;

/** What a typed period label is wrong about; null when the label is usable. */
type PeriodLabelError = "unparseable" | "duplicate" | null;

/**
 * What this screen may do with a delivery. An unshipped one is planned (`open`). A shipped or voided
 * one is history, whose periods the backend refuses to change (RV2-plan-editing-06): a super admin
 * may still correct its window and price (`correctable`, `can_correct_shipped`), anyone else only
 * sees it (`history`), and then it is left out of the request entirely.
 */
type DeliveryMode = "open" | "correctable" | "history";

/**
 * One production period of one delivery: how much is planned there, and how much of that is
 * already backed.
 *
 * `planned` is the only editable number — backed volume belongs to a commitment and is managed on
 * the deal page, so the open remainder `planned - backedMwh` is what the save writes.
 */
interface PeriodRow {
  key: string;
  /** `{period_start}/{period_end}` from the wire; null for a row whose label the user is typing. */
  periodKey: string | null;
  periodLabel: string;
  isNew: boolean;
  backedMwh: number;
  planned: number | null;
}

interface DeliveryForm {
  key: string;
  id: number | null;
  windowStart: string;
  windowEnd: string;
  unitPrice: string | number;
  removed: boolean;
  mode: DeliveryMode;
  status: TransactionStatus | null;
  /** The delivery as the deal holds it; null for one added on this screen. */
  saved: DeliveryResponse | null;
  rows: PeriodRow[];
}

/**
 * The plan editor: the deal's shape — its deliveries, their windows and prices, and one planned
 * volume per production period.
 *
 * The deal page's default view owns *backing* (who backs what); this screen owns *how much is
 * planned*. A producer thinks in "how much of Q1 goes in this shipment", so a period is one row
 * with one number, not a backed row plus an open remainder.
 *
 * The form IS the saved payload: it is sent as the plan's end state, carrying the `plan_version` it
 * was hydrated from, and the server works out what to write. Shipped and voided deliveries are left
 * out of it — the backend freezes them, and they are never removed by their absence — unless the
 * session may correct them (`can_correct_shipped`, a super admin's): then their window and price are
 * editable, their periods are not, and the screen says which invoices keep the old numbers. On a
 * completed or cancelled deal that correction is all the screen offers. That same state is what
 * `?dry_run=1` previews for its warnings and its per-card change lines.
 */
const props = withDefaults(
  defineProps<{
    deal: DealResponse;
    /** The last `?dry_run=1` result — its warnings are what the preview is for. */
    preview?: PlanApplyResult | null;
    busy?: boolean;
    errorMessage?: string | null;
  }>(),
  { preview: null, busy: false, errorMessage: null }
);

const emit = defineEmits<{
  save: [state: PlanStateRequest];
  "dry-run": [state: PlanStateRequest];
  discard: [];
}>();

const { t } = useI18n();

const deliveries = ref<DeliveryForm[]>([]);
/** The plan as hydrated, so "dirty" is a comparison against it rather than a pile of edit flags. */
const hydratedDeliveries = ref<PlanDeliveryState[]>([]);
let newDeliveryCount = 0;
let newRowCount = 0;

const visibleDeliveries = computed(() =>
  deliveries.value.filter((delivery) => !delivery.removed)
);

/** Whether the plan itself may change; on a finished deal only corrections of shipped deliveries are left. */
const canPlan = computed(() => props.deal.capabilities.can_manage_commitments);

const correctsHistory = computed(() =>
  deliveries.value.some((delivery) => delivery.mode === "correctable")
);

/** A period below its backed floor, or a period label that cannot be written, blocks the save. */
const hasErrors = computed(() =>
  visibleDeliveries.value.some(
    (delivery) =>
      (!fieldsLocked(delivery) && delivery.windowEnd === "") ||
      (!periodsLocked(delivery) &&
        delivery.rows.some((row) => isRowInvalid(delivery, row)))
  )
);

/** The deliveries this screen states, in the order the request carries them. */
const statedDeliveries = computed(() =>
  visibleDeliveries.value.filter((delivery) => delivery.mode !== "history")
);

/** The plan this screen would save: its end state, stamped with the version it was hydrated from. */
const planState = computed<PlanStateRequest>(() => ({
  plan_version: props.deal.plan_version,
  deliveries: statedDeliveries.value.map(deliveryState),
}));

const isDirty = computed(
  () => !isEqual(planState.value.deliveries, hydratedDeliveries.value)
);

const canSave = computed(() => isDirty.value && !hasErrors.value);

// An edit that fails validation is unsaved work too, even though it cannot be saved yet.
useUnsavedChangesGuard(() => isDirty.value || hasErrors.value);

/** Warnings about a delivery this screen does not state — a shipped one, usually. */
const dealWarnings = computed(() =>
  orderWarnings(props.preview?.warnings ?? [])
);

/**
 * The card keys in the order the last preview request carried them, so an answer keyed by request
 * position finds the card it was computed for even after a card was added or removed since.
 * Null until this screen has asked for a preview; a preview handed in from outside then maps by the
 * current order.
 */
let previewKeys: string[] | null = null;

/** The preview's answer for this card: the outcome at the position its key had in that request. */
const outcomeFor = (delivery: DeliveryForm) => {
  const keys =
    previewKeys ?? statedDeliveries.value.map((candidate) => candidate.key);
  const index = keys.indexOf(delivery.key);
  return index === -1 ? null : (props.preview?.deliveries[index] ?? null);
};

const warningsFor = (delivery: DeliveryForm) =>
  orderWarnings(outcomeFor(delivery)?.warnings ?? []);

/** Said as soon as a correction is typed, not only once the dry run answers. */
const correctionCaveatsFor = (delivery: DeliveryForm): string[] => {
  if (delivery.mode !== "correctable" || delivery.saved === null) return [];
  const saved = delivery.saved;
  return correctionCaveats(t, saved, {
    priceChanged: !samePrice(blankToNull(delivery.unitPrice), saved.unit_price),
    windowChanged:
      blankToNull(delivery.windowStart) !== saved.delivery_window_start ||
      delivery.windowEnd !== saved.delivery_window_end,
  });
};

/** The window and price inputs: open to a correction, or to planning while the plan may change. */
const fieldsLocked = (delivery: DeliveryForm) =>
  delivery.mode === "history" || (delivery.mode === "open" && !canPlan.value);

const periodsLocked = (delivery: DeliveryForm) =>
  delivery.mode !== "open" || !canPlan.value;

const changesFor = (delivery: DeliveryForm) =>
  outcomeFor(delivery)?.changes ?? [];

const isBelowBacked = (row: PeriodRow) => plannedOf(row) < row.backedMwh;

const periodLabelError = (
  delivery: DeliveryForm,
  row: PeriodRow
): PeriodLabelError => {
  const label = row.periodLabel.trim();
  if (label === "") return plannedOf(row) > 0 ? "unparseable" : null;
  if (parsePeriodLabel(label) === null) return "unparseable";
  return isDuplicatePeriod(delivery, row) ? "duplicate" : null;
};

/** Why this period cannot be dropped here; undefined leaves the button plain and enabled. */
const removeBlockedReason = (row: PeriodRow) =>
  row.backedMwh > 0
    ? t("forwards.deals.editor.remove_row_backed", {
        mwh: formatNumberWithSpaces(row.backedMwh),
      })
    : undefined;

const addDelivery = () => {
  newDeliveryCount += 1;
  deliveries.value.push({
    key: `new-delivery-${newDeliveryCount}`,
    id: null,
    windowStart: "",
    windowEnd: "",
    unitPrice: "",
    removed: false,
    mode: "open",
    status: null,
    saved: null,
    rows: [],
  });
};

const removeDelivery = (delivery: DeliveryForm) => {
  if (delivery.id === null) {
    deliveries.value = deliveries.value.filter(
      (candidate) => candidate.key !== delivery.key
    );
    return;
  }
  delivery.removed = true;
};

const addPeriod = (delivery: DeliveryForm) => {
  newRowCount += 1;
  delivery.rows.push({
    key: `new-period-${newRowCount}`,
    periodKey: null,
    periodLabel: "",
    isNew: true,
    backedMwh: 0,
    planned: null,
  });
};

/** Planning nothing is how a period leaves the plan; a row the user added simply goes away. */
const removePeriod = (delivery: DeliveryForm, row: PeriodRow) => {
  if (row.isNew) {
    delivery.rows = delivery.rows.filter(
      (candidate) => candidate.key !== row.key
    );
    return;
  }
  row.planned = 0;
};

const discard = () => {
  hydrate();
  emit("discard");
};

const runDryRun = debounce((state: PlanStateRequest, keys: string[]) => {
  previewKeys = keys;
  emit("dry-run", state);
}, DRY_RUN_DEBOUNCE_MS);

watch(
  [planState, hasErrors, isDirty],
  ([state, errors, dirty], [previousState, previousErrors]) => {
    if (isEqual(state, previousState) && errors === previousErrors) return;
    if (errors || !dirty) {
      runDryRun.cancel();
      return;
    }
    runDryRun(
      state,
      statedDeliveries.value.map((delivery) => delivery.key)
    );
  },
  { deep: true }
);

// A metadata save or an escrow return hands back a new deal object around the same plan, and
// rebuilding the form then would only throw away what the user is typing. What this form holds
// changes only with a plan write, which bumps `plan_version`, or with a delivery shipping.
watch(
  [
    () => props.deal.id,
    () => props.deal.plan_version,
    () => frozenDeliveryIds(props.deal).join(","),
  ],
  hydrate,
  { immediate: true }
);

function hydrate(): void {
  newDeliveryCount = 0;
  newRowCount = 0;
  runDryRun.cancel();
  previewKeys = null;
  deliveries.value = props.deal.deliveries.map((delivery) => ({
    key: `d${delivery.id}`,
    id: delivery.id,
    windowStart: delivery.delivery_window_start ?? "",
    windowEnd: delivery.delivery_window_end,
    unitPrice: delivery.unit_price ?? "",
    removed: false,
    mode: modeOf(delivery),
    status: delivery.transaction_status,
    saved: delivery,
    rows: periodRowsOf(delivery),
  }));
  hydratedDeliveries.value = props.deal.deliveries
    .filter((delivery) => modeOf(delivery) !== "history")
    .map(deliveryStateOf);
}

function modeOf(delivery: DeliveryResponse): DeliveryMode {
  if (!isFrozen(delivery)) return "open";
  return props.deal.capabilities.can_correct_shipped
    ? "correctable"
    : "history";
}

/**
 * A frozen delivery's periods are refused server-side, and the save is all-or-nothing — one stray
 * keystroke on such a row would throw away every other edit on the screen (RV2-plan-editing-06).
 */
function isFrozen(delivery: DeliveryResponse): boolean {
  return isDeliveryFrozen(delivery.transaction_status);
}

function frozenDeliveryIds(dealResponse: DealResponse): number[] {
  return dealResponse.deliveries
    .filter(isFrozen)
    .map((delivery) => delivery.id);
}

/**
 * One row per exact `(period_start, period_end)` the delivery's slices carry.
 *
 * Widths that overlap — a quarter and a month inside it — are different periods and stay different
 * rows: the sum that means anything is the one per exact period, never per calendar month.
 */
function periodRowsOf(delivery: DeliveryResponse): PeriodRow[] {
  const byPeriod = new Map<string, PeriodRow>();
  for (const slice of delivery.slices) {
    const key = `${slice.period_start}/${slice.period_end}`;
    const row = byPeriod.get(key) ?? {
      key,
      periodKey: key,
      periodLabel: slice.period_label,
      isNew: false,
      backedMwh: 0,
      planned: 0,
    };
    if (!slice.is_open) row.backedMwh += slice.volume_mwh;
    row.planned = (row.planned ?? 0) + slice.volume_mwh;
    byPeriod.set(key, row);
  }
  return [...byPeriod.values()];
}

/** A cleared field plans nothing, exactly like a typed 0 — and exactly like removing the row. */
function plannedOf(row: PeriodRow): number {
  return row.planned ?? 0;
}

/**
 * The period a row addresses, as `{start}/{end}`.
 *
 * A row the user is typing has no wire period yet, so its bounds come from the label — the same
 * key space, because a slice's `period_label` is the label of its own bounds.
 */
function periodKeyOf(row: PeriodRow): string | null {
  if (row.periodKey !== null) return row.periodKey;
  const start = periodStartDate(row.periodLabel.trim());
  const end = periodEndDate(row.periodLabel.trim());
  return start !== null && end !== null ? `${start}/${end}` : null;
}

/**
 * Two rows for one period cannot both be its planned volume — the backend refuses a period stated
 * twice, and the user would not know which of the two rows it read.
 */
function isDuplicatePeriod(delivery: DeliveryForm, row: PeriodRow): boolean {
  const key = periodKeyOf(row);
  if (key === null) return false;
  return delivery.rows.some(
    (candidate) => candidate.key !== row.key && periodKeyOf(candidate) === key
  );
}

function isRowInvalid(delivery: DeliveryForm, row: PeriodRow): boolean {
  return isBelowBacked(row) || periodLabelError(delivery, row) !== null;
}

/**
 * One delivery as the request states it: its window, its price, and one planned volume per period.
 *
 * Only the rows that address a production period travel: a row left blank plans nothing anywhere,
 * so it is not part of the plan. A row whose label the user has half-typed is a validation error
 * that keeps the whole save from being sent, so it never reaches here with a usable volume.
 */
function deliveryState(delivery: DeliveryForm): PlanDeliveryState {
  return {
    id: delivery.id,
    delivery_window_start: blankToNull(delivery.windowStart),
    delivery_window_end: delivery.windowEnd,
    unit_price: blankToNull(delivery.unitPrice),
    periods: delivery.rows.flatMap((row) => {
      const period = periodLabelOf(row);
      return period === null ? [] : [{ period, planned_mwh: plannedOf(row) }];
    }),
  };
}

/** The period a row states, trimmed; null when there is nothing parseable to state. */
function periodLabelOf(row: PeriodRow): string | null {
  const label = row.periodLabel.trim();
  return label !== "" && parsePeriodLabel(label) !== null ? label : null;
}

/** The price floor is the most actionable hint the dry run has, so it leads every list. */
function orderWarnings(warnings: PlanWarning[]): PlanWarning[] {
  return [
    ...warnings.filter((warning) => warning.kind === "below_floor_hint"),
    ...warnings.filter((warning) => warning.kind !== "below_floor_hint"),
  ];
}

/** A cleared field is `null`; a touched number input hands back a number, the wire wants a string. */
function blankToNull(value: string | number): string | null {
  const text = String(value).trim();
  return text === "" ? null : text;
}

/** "25", "25.0" and the wire's "25.000" are one price. */
function samePrice(typed: string | null, saved: string | null): boolean {
  return typed === null || saved === null
    ? typed === saved
    : Number(typed) === Number(saved);
}
</script>

<style scoped lang="scss">
.volume-cell {
  min-width: 9rem;
}
</style>
